import { create } from "d3";
import { Entidad } from "../../../data/Entidad";
import type { ICatalogoSATClaveProductServ, ICatalogoSATClaveUnidad } from "../../../data/entidad/Factura";
import type { IFinanzaAgrupadorCargos as IFinanzaAgrupadorCargosOriginal } from "../../../data/entidad/FinanzaAgrupadorCargos";
import { _DICC_ALUMNO } from "../../../data/modulo/Alumno";
import { _DICC_ESCUELA } from "../../../data/modulo/Escuela";
import { _DICC_AGRUPADORES, _SvFinanzasActualizarAgrupadorCargos, _SvFinanzasAsignarCargosAgrupador, _SvFinanzasEliminarAgrupadorCargos, _SvFinanzasNuevoAgrupadorCargos, _SvFinanzasObtenerListaAgrupadorCargos, IAgrupadorEliminarResponse } from "../../../data/modulo/FinanzaAgrupadorCargos";
import { _DICC_FINANZAS_CARGOS, _SvFacturaObtenerClaveProdServSATBase, _SvFacturaObtenerClaveUnidadSATBase } from "../../../data/modulo/FinanzaCargo";
import { MainPage } from "../../../MainPage";
import _L, { _HttpMsgV2 } from "../../../util/Labels";
import { FormGenerator } from "../../controlD3/Formulario";
import { List } from "../../controlD3/List";
import { _ModalConfirmacionBasicoV2 } from "../../controlD3/ModalThings/ModalConfirmacionBasicoV2";
import { _ModalForm } from "../../controlD3/ModalThings/ModalForm";
import { _ModalServiceIteratorBasic } from "../../controlD3/ModalThings/ModalServiceIteratorBasic";
import { _ModalSimple } from "../../controlD3/ModalThings/ModalSimple";
import { ModalThingsUtil } from "../../controlD3/ModalThings/Utils";
import { NotificacionV2 } from "../../controlD3/NotificacionV2";
import { Table } from "../../controlD3/Tabla";
import { TTableMenuDataSelectedOptionConfig } from "../../controlD3/TablaTypes";
import { UIUtilFormat } from "../../util/Format";
import { UIUtilPermission } from "../../util/Permission";
import "./FinanzaAgrupadorCargosModal.scss";
import { UIUtilViewFinanzasIngresoUtil } from "./FinanzasIngresoUtil";

type IFinanzaAgrupadorCargos = IFinanzaAgrupadorCargosOriginal & { NCargosAsignados?: number };

type IAgrupadorCargosForm = IFinanzaAgrupadorCargos & {
    ClaveProdServSAT: string;
    ClaveUnidadSAT: string;
}

const MODULO = Entidad.CModulo.FinanzasFacturacion;
const ACTION_EDIT = Entidad.CAccionPermiso.Editar;

export class FinanzaAgrupadorCargosModal {
    private modal: ModalThingsUtil.IModalThingsV2;
    private table: Table.Tabla<IFinanzaAgrupadorCargos>;
    private currentIdEscuelaSelected: number;

    constructor() {
        const escuelasDisponiblesList = Array.from(_DICC_ESCUELA.values())
            .filter(d => (
                d.UsaFinanzas && UIUtilPermission._HasModulePermission(MODULO, d.IdKinder)
            ))
        this.currentIdEscuelaSelected = escuelasDisponiblesList[0]?.IdKinder || 0;

        new _ModalSimple({
            Title: _L("finanzascargo.agrupador.modal_title"),
            Message: _L("finanzascargo.agrupador.modal_description"),
            Width: 1000,
            DrawContent: (content, mt) => {
                this.modal = mt;
                const mainContent = content.classed("finanza-agrupador-cargos-modal", true).append("div").attr("class", "main-content");
                const escuelasListContainer = mainContent.append("div").classed("escuelas-list-container", true);
                const agrupadoresListContainer = mainContent.append("div").classed("agrupadores-table-container", true);

                const escuelasListControl = new List<Entidad.IEscuela>()
                    ._SetItems(escuelasDisponiblesList)
                    ._SetParent(escuelasListContainer)
                    ._SetUpdateItem((content, datum) => content.text(datum.Nombre)
                        .classed("active", kinder => kinder.IdKinder === this.currentIdEscuelaSelected)
                        .on("click", () => {
                            this.currentIdEscuelaSelected = datum.IdKinder;
                            this.LoadData();
                            escuelasListControl._RefreshList();
                        })
                    )
                    ._RefreshList()

                const fnRowsActions = () => {
                    const actions: TTableMenuDataSelectedOptionConfig<IFinanzaAgrupadorCargos>[] = [];
                    if (this.CheckActionPermission()) {
                        actions.push({
                            Label: _L("finanzascargo.agrupador.action_asignarcargos"),
                            MultiData: false,
                            Callback: (datos) => this.OpenAsignarAgrupadorACargos(datos[0])
                        });
                    }
                    if (this.CheckActionPermission()) {
                        actions.push({
                            Label: _L("c_actions.editar"),
                            MultiData: false,
                            Callback: (datos) => this.OpenEditarForm(datos[0])
                        });
                    }
                    if (this.CheckActionPermission()) {
                        actions.push({
                            Label: _L("c_actions.eliminar"),
                            MultiData: true,
                            Callback: (datos) => this.OpenEliminar(datos)
                        });
                    }
                    return actions;
                }

                this.table = new Table.Tabla<IFinanzaAgrupadorCargos>({
                    IdData: "Id",
                    IdTabla: "FinanzaAgrupadorCargosModal-Table",
                    Parent: agrupadoresListContainer,
                    RenderColumnHeadings: null,
                    MinWidth: 550,
                    Columns: [
                        { Field: "Prefix", Label: _L("finanzascargo.agrupador.prefix"), Width: "35%", MinWidth: "150px" },
                        { Field: "IncluyeAlumno", Label: _L("finanzascargo.agrupador.incluye_alumno"), Width: "5%", MinWidth: "80px", RenderCell: (r) => r ? _L("general.afirma") : _L("general.niega") },
                        { Field: "IncluyePeriodo", Label: _L("finanzascargo.agrupador.incluye_periodo"), Width: "5%", MinWidth: "80px", RenderCell: (r) => r ? _L("general.afirma") : _L("general.niega") },
                        { Field: "ProdServSAT", Label: _L("finanzascargo.agrupador.prodserv_sat"), Width: "25%", MinWidth: "150px", RenderCell: (r) => `${r[0]} - ${r[1]}` },
                        { Field: "UnidadSAT", Label: _L("finanzascargo.agrupador.unidad_sat"), Width: "25%", MinWidth: "150px", RenderCell: (r) => `${r[0]} - ${r[1]}` },
                        { Field: "NCargosAsignados", Label: _L("finanzascargo.agrupador.cargos_asignados"), Width: "5%", MinWidth: "80px" }
                    ],
                    OptionsTopDefaultV2: {
                        Options: this.CheckActionPermission()
                            ? [{
                                Label: _L("c_actions.agregar"),
                                Callback: () => this.OpenAgregarForm()
                            }]
                            : []
                    },
                    OptionsOfDataCheckV3: () => ({
                        Options: fnRowsActions()
                    }),
                    EvaluatorAndSubLevelsBuild: {
                        GetOptionsInRowV2: () => ({
                            Options: fnRowsActions()
                        })
                    }
                });
            }
        });

        this.LoadData();
    }

    // *****************************************************************************
    // Formularios Agrupador Cargos
    // *****************************************************************************

    private OpenAgregarForm() {
        _ModalForm({
            GetForm: () => this.GetFormAgrupador(),
            Title: _L("finanzascargo.agrupador.action_addagrupador_title"),
            Width: 520,
            OnAccept: (form) => {
                const data = form._Data;
                return _SvFinanzasNuevoAgrupadorCargos(this.currentIdEscuelaSelected, {
                    Prefix: data.Prefix,
                    IncluyeAlumno: data.IncluyeAlumno,
                    IncluyePeriodo: data.IncluyePeriodo,
                    ProdServSAT: data.ProdServSAT,
                    UnidadSAT: data.UnidadSAT,
                }).then(res => {
                    if (res.Resultado > 0 || res.Resultado === -1) this.LoadData();
                    return res;
                })
            }
        })
    }

    private OpenEditarForm(data: IFinanzaAgrupadorCargos) {
        _ModalForm({
            Title: _L("finanzascargo.agrupador.action_editagrupador_title"),
            Width: 520,
            GetForm: () => {
                const form = this.GetFormAgrupador();
                form._AsignaData({
                    ...data,
                    ClaveProdServSAT: data.ProdServSAT[0],
                    ClaveUnidadSAT: data.UnidadSAT[0],
                });
                return form;
            },
            OnAccept: (form, mtForm) => {
                const data = form._Data;

                _ModalConfirmacionBasicoV2({
                    Title: _L("finanzascargo.agrupador.action_editagrupador_cargosafectados_title"),
                    Content: (() => {
                        const msgConfirm = _L("finanzascargo.agrupador.action_editagrupador_cargosafectar_confirm");
                        // Buscar si hay cargos (plantillas) que serán afectados, para mostrar en lista
                        const cargosAfectarList = Array.from(_DICC_FINANZAS_CARGOS.values())
                            .filter(c => c.IdAgrupador === data.Id);

                        if (cargosAfectarList.length === 0) return msgConfirm;

                        const content = document.createElement("div");
                        content.append(msgConfirm);
                        content.append(document.createElement("br"));
                        content.append(document.createElement("br"));
                        content.append(document.createElement("hr"));
                        new List<Entidad.IFinanzaCargo>()
                            ._SetUpdateItem((content, cargo) => content.text(cargo.Nombre || "-"))
                            ._SetItems(cargosAfectarList)
                            ._SetParent(content)
                        return content;
                    })()
                }).then(confirmacion => {
                    if (!confirmacion) return;
                    _SvFinanzasActualizarAgrupadorCargos({
                        Id: data.Id,
                        Prefix: data.Prefix,
                        IncluyeAlumno: data.IncluyeAlumno,
                        IncluyePeriodo: data.IncluyePeriodo,
                        ProdServSAT: data.ProdServSAT,
                        UnidadSAT: data.UnidadSAT,
                    }).then(res => {
                        if (res.Resultado > 0 || res.Resultado === -1) {
                            this.LoadData();

                            if (res.NCargosPlantillaModificados > 0) {
                                MainPage._ReloadService(Entidad.CTipoRequest.FinanzaCargo, this.currentIdEscuelaSelected);
                                _ModalConfirmacionBasicoV2({
                                    Title: _L("finanzascargo.agrupador.action_editagrupador_cargosafectados_title"),
                                    Content: _L("finanzascargo.agrupador.action_editagrupador_cargosafectados_message", res.NCargosPlantillaModificados),
                                    Width: 300,
                                    Calcelable: false,
                                }).then(() => {
                                    mtForm.Modal._Ocultar();
                                })
                            }
                            NotificacionV2._Mostrar(_HttpMsgV2(res, "editar"), res.Resultado > 0 ? "INFO" : "ADVERTENCIA");
                        }
                    })
                });
                return null;
            }
        });
    }

    private OpenEliminar(data: IFinanzaAgrupadorCargos[]) {
        let msgCargosDesasignar = _L("finanzascargo.agrupador.action_eliminaragrupador_description");
        // Buscar si hay cargos (plantillas) que serán desasignados
        const cargosDesasignarList = Array.from(_DICC_FINANZAS_CARGOS.values())
            .filter(c => data.some(a => a.Id === c.IdAgrupador));

        if (cargosDesasignarList.length > 0) {
            msgCargosDesasignar += "\n\n" + _L("finanzascargo.agrupador.action_eliminaragrupador_cargosdesasignar_confirm");
        }

        // Abrir modal de confirmación
        _ModalServiceIteratorBasic<IFinanzaAgrupadorCargos>({
            Title: _L("finanzascargo.agrupador.action_eliminaragrupador_title"),
            Message: msgCargosDesasignar,
            OnDrawContent: (content) => {
                if (cargosDesasignarList.length === 0) return;
                content.append("br")
                content.append("br")
                content.append("hr")
                new List<Entidad.IFinanzaCargo>()
                    ._SetUpdateItem((content, cargo) => content.text(cargo.Nombre || "-"))
                    ._SetItems(cargosDesasignarList)
                    ._SetParent(content)
            },
            Width: 400,
            DataToProccess: data,
            OnError_GetItemDataTag: d => d.Prefix,
            TypeRequest: null,
            OnStepAProccess: (d) => _SvFinanzasEliminarAgrupadorCargos(d.Id),
            OnEndProccess: (items) => {
                let hasSuccess = false;
                let nCargosDesasignados = 0;
                items.forEach((d: IAgrupadorEliminarResponse) => {
                    if (d.Resultado > 0 || d.Resultado === -1) hasSuccess = true;
                    nCargosDesasignados += (d.NCargosPlantillaDesasignados || 0);
                });
                if (hasSuccess) this.LoadData();
                if (nCargosDesasignados > 0) {
                    MainPage._ReloadService(Entidad.CTipoRequest.FinanzaCargo, this.currentIdEscuelaSelected);
                    _ModalConfirmacionBasicoV2({
                        Title: _L("finanzascargo.agrupador.action_eliminaragrupador_cargosdesasignados_title"),
                        Content: _L("finanzascargo.agrupador.action_eliminaragrupador_cargosdesasignados_message", nCargosDesasignados),
                        Width: 300,
                        Calcelable: false
                    })
                }
            }
        });
    }

    private GetFormAgrupador(): FormGenerator<IAgrupadorCargosForm> {
        const previewContainer: HTMLDivElement = create("div").attr("class", "preview").node()
        const fnUpdatePreview = () => {
            previewContainer.textContent = "";
            const preview = this.GetConceptoPreview(form._Data);
            previewContainer.appendChild(preview);

        }
        const form = new FormGenerator<IAgrupadorCargosForm>()
            ._Crear({
                OnLoad: (container) => {
                    container.append(() => previewContainer)
                },
                OnAssignData: () => setTimeout(() => fnUpdatePreview()),
                schema: [
                    {
                        model: "Prefix", type: "input", labelText: _L("finanzascargo.agrupador.prefix"),
                        inputAttr: { required: true, placeholder: _L("finanzascargo.agrupador.prefix_placeholder"), maxlength: 100, oninput: () => fnUpdatePreview() }
                    },
                    {
                        model: "IncluyeAlumno", type: "input", labelText: _L("finanzascargo.agrupador.incluye_alumno"), inputAttr: { type: "checkbox", onchange: () => fnUpdatePreview() }
                    },
                    {
                        model: "IncluyePeriodo", type: "input", labelText: _L("finanzascargo.agrupador.incluye_periodo"), inputAttr: { type: "checkbox", onchange: () => fnUpdatePreview() }
                    },
                    {
                        model: "ClaveProdServSAT",
                        type: "selectMaterial",
                        labelText: _L("finanzascargo.frm1_claveprodservsat"),
                        textInfo: _L("finanzascargo.claveprodservsat_info"),
                        selectMaterialAttr: {
                            valueMember: "key",
                            displayMember: "full_name",
                            required: true,
                            ShowAndEnableSearchText: true,
                            RecyclerView: true,
                            ListWidth: "450px",
                            OnChangeSearchText_GetDataValueToEval: ({ key, name }: ICatalogoSATClaveProductServ) => (key + name),
                            Data: _SvFacturaObtenerClaveProdServSATBase(this.currentIdEscuelaSelected, null).then(res => {
                                return (res.Datos || []).map(d => ({ ...d, full_name: `${d.key} - ${d.name}` })); // +full_name
                            }),
                            onChange: (_, d: ICatalogoSATClaveProductServ) => {
                                form._DataOrigin.ProdServSAT = [d.key, d.name];
                                fnUpdatePreview();
                            }
                        }
                    },
                    {
                        model: "ClaveUnidadSAT",
                        type: "selectMaterial",
                        labelText: _L("finanzascargo.frm1_claveunidadsat"),
                        textInfo: _L("finanzascargo.claveunidadsat_info"),
                        selectMaterialAttr: {
                            valueMember: "key",
                            displayMember: "full_name",
                            required: true,
                            ShowAndEnableSearchText: true,
                            RecyclerView: true,
                            ListWidth: "450px",
                            OnChangeSearchText_GetDataValueToEval: ({ key, name }: ICatalogoSATClaveUnidad) => (key + name),
                            Data: () => _SvFacturaObtenerClaveUnidadSATBase(this.currentIdEscuelaSelected, null).then(res => {
                                return (res.Datos || []).map(d => ({ ...d, full_name: `${d.key} - ${d.name}` })); // Agregar full_name para el displayMember
                            }),
                            onChange: (_, d: ICatalogoSATClaveUnidad) => {
                                form._DataOrigin.UnidadSAT = [d.key, d.name];
                                fnUpdatePreview();
                            }
                        }
                    }
                ]
            });
        return form;
    }

    private GetConceptoPreview(config: IFinanzaAgrupadorCargos): HTMLElement {
        const container = create("div").attr("class", "finanza-agrupador-cargos-modal-preview");
        container.append("label").text(`${_L("general.preview")} (${_L("general.example")})`);
        container.append("hr");
        const tbl = container.append("table");
        const conceptoStr = (() => {
            let str = config.Prefix;
            if (config.IncluyeAlumno) {
                const alumnoExample = Array.from(_DICC_ALUMNO.values()).find(d => d.IdKinder == this.currentIdEscuelaSelected)
                console.warn(alumnoExample)
                str += ` ${alumnoExample?.NombreCompleto || "Jorge Ibargüengoitia"}`
            }
            if (config.IncluyePeriodo) {
                str += ` Enero 2026`
            }
            return str
        })()
        tbl.html(`<tr>
            <th>${_L("finanzascargo.agrupador.prev_cantidad")}</th>
            <th>${_L("finanzascargo.agrupador.prev_descripcion")}</th>
            <th>${_L("finanzascargo.agrupador.prev_prodserv")}</th>
            <th>${_L("finanzascargo.agrupador.prev_unidad")}</th>
            <th>${_L("finanzascargo.agrupador.prev_preciouni")}</th>
        </tr>
        <tr>
            <td>1</td>
            <td>${conceptoStr || "-"}</td>
            <td>${config.ProdServSAT ? config.ProdServSAT[0] || "-" : "-"}</td>
            <td>${config.UnidadSAT ? `${config.UnidadSAT[0]}/${config.UnidadSAT[1]}` : "-"}</td>
            <td>$1000.00</td>
        </tr>`);
        return container.node();
    }

    // *****************************************************************************
    // Asignacion a cargos
    // *****************************************************************************

    private OpenAsignarAgrupadorACargos(agrupador: IFinanzaAgrupadorCargos) {
        const categoriasAsignables = [Entidad.CFinanzaCargoCategoria.Cargo, Entidad.CFinanzaCargoCategoria.CargoEntrada, Entidad.CFinanzaCargoCategoria.CargoSalida];
        const cargosAsignablesList = Array.from(_DICC_FINANZAS_CARGOS.values())
            .filter(c => c.IdEscuela === this.currentIdEscuelaSelected
                && categoriasAsignables.includes(c.Categoria));
        const cargosAsignadosIds = new Set(cargosAsignablesList.filter(c => c.IdAgrupador === agrupador.Id).map(c => c.ID));

        _ModalForm({
            Title: _L("finanzascargo.agrupador.action_asignarcargos"),
            Width: 400,
            GetForm: () => new FormGenerator<{ Agrupador: string; CargosAsignados: number[] }>()
                ._Crear({
                    schema: [
                        {
                            model: "Agrupador", type: "input", labelText: _L("finanzascargo.agrupador.agrupador"),
                            inputAttr: { value: agrupador.Prefix, required: true, disabled: true, removeBorder: true }
                        },
                        {
                            model: "CargosAsignados", type: "selectMaterial",
                            labelText: _L("finanzascargo.agrupador.cargos_asignados"),
                            selectMaterialAttr: {
                                valueMember: "ID",
                                displayMember: "Nombre",
                                multiselect: true,
                                ListWidth: "350px",
                                Data: cargosAsignablesList.sort((a, b) => (
                                    (Number(cargosAsignadosIds.has(b.ID)) - Number(cargosAsignadosIds.has(a.ID))) || a.Nombre.localeCompare(b.Nombre)
                                )),
                                OnStepItemListUI: (content, datum: Entidad.IFinanzaCargo) => {
                                    let strHTML = `${datum.Nombre || "-"}`;
                                    strHTML += `<br><i">${UIUtilFormat._CurrencyFmt(datum.Valor)}</i>`;
                                    content.classed("finanza-agrupador-asignar-cargo-item", true).html(strHTML);
                                    if (datum.IdAgrupador > 0) {
                                        const tag = UIUtilViewFinanzasIngresoUtil._CreaAgrupadorCargosTag(datum.IdEscuela, datum.IdAgrupador);
                                        content.append(() => tag._ControlContainer);
                                    }
                                },
                                ShowAndEnableSearchText: true,
                                OnChangeSearchText_GetDataValueToEval: (d: Entidad.IFinanzaCargo) => [
                                    d.Nombre, d.ClaveProdServSAT, d.NombreProdServSAT, d.ClaveUnidadSAT, d.NombreUnidadSAT, d.Valor,
                                    (_DICC_AGRUPADORES.get(d.IdEscuela)?.get(d.IdAgrupador)?.Prefix || "")
                                ].toString(),
                            }
                        }
                    ]
                }, {
                    Agrupador: agrupador.Prefix,
                    CargosAsignados: Array.from(cargosAsignadosIds),
                }),
            OnAccept: (form, mt) => {
                const cargosAsignadosFinal = form._Data.CargosAsignados;
                const cargosNuevos: number[] = [];
                const cargosRemovidos: number[] = [];
                cargosAsignablesList.forEach(c => {
                    const isInForm = cargosAsignadosFinal.includes(c.ID);
                    const isInCurrent = cargosAsignadosIds.has(c.ID);
                    if (isInForm && !isInCurrent) cargosNuevos.push(c.ID);
                    if (!isInForm && isInCurrent) cargosRemovidos.push(c.ID);
                });
                if (cargosNuevos.length === 0 && cargosRemovidos.length === 0) {
                    _ModalConfirmacionBasicoV2({
                        Title: _L("general.no_changes_save"),
                        Content: _L("general.no_changes_form_message"),
                        Width: 300,
                        Calcelable: false,
                    })
                }
                else {
                    _ModalConfirmacionBasicoV2({
                        Title: _L("finanzascargo.agrupador.action_asignarcargos"),
                        Content: (() => {
                            const content = document.createElement("div");
                            if (cargosNuevos.length > 0) {
                                const p = document.createElement("p");
                                p.textContent = _L("finanzascargo.agrupador.action_asignaragrupador_confirm_nuevos", cargosNuevos.length);
                                content.appendChild(p);
                            }
                            if (cargosRemovidos.length > 0) {
                                const p = document.createElement("p");
                                p.textContent = _L("finanzascargo.agrupador.action_asignaragrupador_confirm_removidos", cargosRemovidos.length);
                                content.appendChild(p);
                            }
                            return content;
                        })(),
                        Width: 260,
                    }).then(confirmacion => {
                        if (!confirmacion) return;
                        _SvFinanzasAsignarCargosAgrupador(agrupador.Id, cargosNuevos, cargosRemovidos).then(async res => {
                            if (res.Resultado > 0 || res.Resultado === -1) {
                                mt.Modal._Ocultar();
                                await MainPage._ReloadServiceAndAwait(Entidad.CTipoRequest.FinanzaCargo, this.currentIdEscuelaSelected);
                                this.LoadData();
                            }
                            NotificacionV2._Mostrar(_HttpMsgV2(res), res.Resultado > 0 ? "INFO" : "ADVERTENCIA");
                        })
                    });
                }
                return null;
            }
        })
    }

    // *****************************************************************************
    // Carga de datos
    // *****************************************************************************

    private LoadData() {
        if (!(this.currentIdEscuelaSelected > 0)) return;

        this.modal.Progress.node()._Visible = true;
        _SvFinanzasObtenerListaAgrupadorCargos(this.currentIdEscuelaSelected).then(res => {
            const datos: IFinanzaAgrupadorCargos[] = (res.Datos || [])
                .map(d => ({
                    ...d,
                    NCargosAsignados: Array.from(_DICC_FINANZAS_CARGOS.values()).filter(c => c.IdAgrupador === d.Id).length,
                }))
                .sort((a, b) => new Date(b.Modificacion).getTime() - new Date(a.Modificacion).getTime()); // Ordenar por fecha de modificación (Desc)
            this.table._UpdateData(datos);
            this.modal.Progress.node()._Visible = false;
        })
    }

    // *****************************************************************************
    // Permisos
    // *****************************************************************************

    private CheckActionPermission(): boolean {
        return UIUtilPermission._HasAccionPermission(ACTION_EDIT, MODULO, this.currentIdEscuelaSelected);
    }
}